// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { Button, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BasePagination, BaseStatus, ConfirmModal, Loading } from '~/components/common';
import { IncidentFormModal } from '~/components/module/job';
// Others
import { IIncident } from '~/utils/interface/incident';
import { getDetailJob } from '~/thunks/job/jobThunk';
import { deleteIncident } from '~/thunks/incident/incidentSlice';
import { incidentActions } from '~/thunks/incident/incidentThunk';
import { IParamQueryJobDetail } from '~/utils/interface/jobs';
import { formatDate, formatTitleCase } from '~/utils/helper/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DateFormatEnum, TypeJobDetailEnum } from '~/utils/enum/common';
import { IListDataResponse, IPaginationResponse } from '~/utils/interface/base';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './IncidentJobTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const IncidentJobTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshIncidentList } = useAppSelector((state) => state.incident);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [paramObject, setParamObject] = useState<IParamQueryJobDetail>({
    jobId: jobId || EMPTY_STRING,
    type: TypeJobDetailEnum.INCIDENTS,
    query: {
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
    },
  });

  const [listIncidents, setListIncidents] = useState<IIncident[]>([]);

  const [idIncident, setIdIncident] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListIncident(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshIncidentList) return;

    handleGetListIncident(paramObject);

    dispatch(incidentActions.setRefreshIncidentList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshIncidentList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListIncident = (params: IParamQueryJobDetail) => {
    setIsLoading(true);

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res?.data as IListDataResponse<IIncident[]>;

        if (!responses) return;

        setPagination(pagination);
        setListIncidents(responses);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    setIsShowModal(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteIncident(idIncident))
      .unwrap()
      .then(() => {
        setIdIncident(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(incidentActions.setRefreshIncidentList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    setIsShowModal(false);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({
      ...paramObject,
      query: {
        ...paramObject.query,
        page,
      },
    });
  };
  //#endregion Handle Function

  return (
    <Stack id='infoJobTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <Typography className={cx('title')}>{t('job_detail_title_recent_incidents')}</Typography>

        <Button
          type='button'
          variant='contained'
          className={cx('buttonAction')}
          onClick={handleAdd}
          startIcon={<img src={icons.commonIconPlus} alt={t('common_alt_img', { ns: 'common/base' })} />}
        >
          {t('job_detail_title_log_new_incident')}
        </Button>
      </Stack>

      <Stack className={cx('body')}>
        {listIncidents && listIncidents?.length > 0 ? (
          listIncidents?.map((item, index) => {
            return (
              <Stack className={cx('item')} key={index}>
                <Stack>
                  <Stack direction={'row'} alignItems={'center'} gap={5}>
                    <Typography className={cx('type')}>{formatTitleCase(item?.type)}</Typography>

                    {item?.priority && <BaseStatus label={item?.priority} />}
                  </Stack>

                  <Typography className={cx('date')}>
                    {formatDate(item?.createdAt, DateFormatEnum.MM_DD_YYY_HH_MM_SS)}
                  </Typography>
                </Stack>

                <Stack direction={'row'} gap={6} alignItems={'center'}>
                  <Typography className={cx('description')}>{item?.description || EMPTY_STRING}</Typography>

                  {/* <IconButton
                    className={cx('buttonRemove')}
                    onClick={() => {
                      setIdIncident(item?.id);
                      setIsShowConfirmDelete(true);
                    }}
                  >
                    <img
                      className={cx('icon')}
                      src={icons.commonIconTrash}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                    />
                  </IconButton> */}
                </Stack>
              </Stack>
            );
          })
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Stack>

      {listIncidents && listIncidents?.length > 0 && (
        <BasePagination count={pagination?.totalPages} page={paramObject?.query?.page} onChange={handlePageChange} />
      )}

      {isShowModal && <IncidentFormModal onClose={handleCloseModal} />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default IncidentJobTab;
